// Browser-side client for the AI prompting suggest-edit endpoints. Auth
// + workspace-org-header pipeline lives in `./clientRequest.ts` so slice-
// specific files (`./clientDryRun.ts`) can share it. See
// docs/planning/ai_prompting_suggest_edit.md.

import {
  call,
  OVERRIDES_ACTION,
  SUGGESTIONS_ACTION,
  HISTORY_ACTION,
} from "./clientRequest";

export { OVERRIDES_ACTION, SUGGESTIONS_ACTION, HISTORY_ACTION };

/**
 * Mirrors `MAX_PROMPT_BYTES` in `server/prompts/validatePromptText.ts`. Kept
 * in sync by hand because the client can't import from the server tree.
 * If you change one, change the other.
 */
export const MAX_PROMPT_BYTES = 16 * 1024;

/**
 * UTF-8 byte length for the editor's live character counter. Matches the
 * server-side check in `validatePromptText`.
 */
export function promptByteLength(text: string): number {
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(text).length;
  }
  return text.length;
}

export type PromptRowDTO = {
  prompt_key: string;
  label: string;
  description: string;
  default_text: string;
  placeholders: readonly string[];
  /**
   * dev-sync b02575e7 (B): true when catalog marks this prompt as requiring
   * peer review even for trainers (`server/prompts/catalog.ts` →
   * `requiresSuggestion`). Used to badge the card.
   */
  requires_suggestion: boolean;
  /**
   * dev-sync b02575e7 (B): server-derived for the calling user — true when
   * the user holds a break-glass role OR the prompt isn't review-required.
   * The client uses this (rather than recomputing from roles) to decide
   * whether to show the "Edit directly" button.
   */
  can_direct_edit: boolean;
  /**
   * Slice C: number of restorable snapshots in `ai_prompt_override_history`
   * for this org/prompt. Used to hide the "Versions" button when there's
   * nothing to restore yet.
   */
  history_count: number;
  /**
   * Slice D: whether the "Test draft" button can run against a registered
   * canonical fixture for this prompt key. Drives client-side button
   * visibility — false keys (e.g. kb_split_v1) hide the button entirely.
   */
  has_dry_run_fixture: boolean;
  /**
   * Slice F: rolling 24h telemetry summary used to render a health pill
   * on the card header. All five outcome buckets are always present so
   * the UI can render `health.by_outcome.all_null` directly without
   * optional chaining. `total === 0` means "no extracts yet in window"
   * and the pill renders as a neutral "No recent activity" state.
   */
  health: {
    window_hours: number;
    total: number;
    by_outcome: {
      success: number;
      upstream_error: number;
      parse_failed: number;
      all_null: number;
      server_error: number;
    };
    failure_rate: number;
    total_failures: number;
  };
  override:
    | {
        id: string;
        text: string;
        updated_by: string | null;
        updated_at: string;
      }
    | null;
  active_text: string;
  source: "override" | "catalog";
};

export type SuggestionDTO = {
  id: string;
  organization_id: string;
  proposed_by_user_id: string;
  prompt_key: string;
  base_prompt_text: string;
  suggested_prompt_text: string;
  note: string | null;
  status: "open" | "accepted" | "rejected" | "merged";
  decided_by_user_id: string | null;
  decided_at: string | null;
  decision_note: string | null;
  merged_text: string | null;
  created_at: string;
  updated_at: string;
};

export type FreshAchievementDTO = {
  id?: string;
  achievement_key?: string;
} & Record<string, unknown>;

export async function listPrompts() {
  return call<{ ok: true; prompts: PromptRowDTO[] }>(OVERRIDES_ACTION, { method: "GET" });
}

export async function saveOverride(promptKey: string, text: string) {
  return call<{ ok: true; override: PromptRowDTO["override"] }>(OVERRIDES_ACTION, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prompt_key: promptKey, text }),
  });
}

export async function revertOverride(promptKey: string) {
  const url = `${OVERRIDES_ACTION}&prompt_key=${encodeURIComponent(promptKey)}`;
  return call<{ ok: true }>(url, { method: "DELETE" });
}

export async function listSuggestions(opts?: { status?: string; promptKey?: string }) {
  const sp = new URLSearchParams();
  if (opts?.status) sp.set("status", opts.status);
  if (opts?.promptKey) sp.set("prompt_key", opts.promptKey);
  const q = sp.toString();
  const url = q ? `${SUGGESTIONS_ACTION}&${q}` : SUGGESTIONS_ACTION;
  return call<{ ok: true; suggestions: SuggestionDTO[] }>(url, { method: "GET" });
}

export async function createSuggestion(promptKey: string, suggestedText: string, note: string | null) {
  return call<{ ok: true; suggestion: SuggestionDTO; fresh_achievements: FreshAchievementDTO[] }>(
    SUGGESTIONS_ACTION,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt_key: promptKey, suggested_text: suggestedText, note }),
    },
  );
}

export type DecideArgs = {
  id: string;
  action: "accept" | "reject" | "merge";
  decisionNote?: string | null;
  mergedText?: string | null;
};

export async function decideSuggestion(args: DecideArgs) {
  return call<{ ok: true; suggestion: SuggestionDTO; fresh_achievements: FreshAchievementDTO[] }>(
    `${SUGGESTIONS_ACTION}&op=decide`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: args.id,
        action: args.action,
        decision_note: args.decisionNote ?? null,
        merged_text: args.mergedText ?? null,
      }),
    },
  );
}

// ── Slice C: override version history ──────────────────────────────────────
//
// Each prompt accumulates a per-org snapshot trail in
// `ai_prompt_override_history`. The editor surfaces these as "Versions" so
// trainers can roll back to a known-good prior text rather than only the
// catalog default. See `server/ai-prompt-handlers/history.ts`.

export type PromptHistorySource = "publish" | "revert" | "restore" | "catalog";

export type PromptHistoryDTO = {
  id: string;
  organization_id: string;
  prompt_key: string;
  text: string;
  source: PromptHistorySource;
  triggered_by_user_id: string | null;
  created_at: string;
};

export async function listOverrideHistory(promptKey: string) {
  const url = `${HISTORY_ACTION}&prompt_key=${encodeURIComponent(promptKey)}`;
  return call<{ ok: true; history: PromptHistoryDTO[] }>(url, { method: "GET" });
}

export async function restoreOverrideFromHistory(promptKey: string, historyId: string) {
  return call<{ ok: true; override: PromptRowDTO["override"] }>(HISTORY_ACTION, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prompt_key: promptKey, history_id: historyId }),
  });
}

// Slice D (dry-run before publish) lives in `./clientDryRun.ts` to keep
// this file under the 260-line soft limit. Re-exported here so any
// existing consumer that lands on the canonical `client.ts` entry point
// keeps working.
export type {
  DryRunFixtureDTO,
  DryRunSuccessDTO,
  DryRunFailureDTO,
} from "./clientDryRun";
export { runDryRun, DRY_RUN_ACTION } from "./clientDryRun";
